import { Controller } from "@hotwired/stimulus"
import { ditherTile } from "../vendor/dither_tile"

const PRESETS = {
  line: { cell: [3.6, 1.2], spread: 1.5, hard: 1 },
  art: { cell: [3.0, 1.1], spread: 1.3, hard: 1 },
  mark: { cell: [2.6, 1.0], spread: 1.0, hard: 1 }
}

let nextId = 0

// Use only on linework or decorative grounds, never on readable content or controls.
export default class extends Controller {
  static values = {
    mode: { type: String, default: "resolve" },
    preset: { type: String, default: "line" },
    from: { type: Number, default: 0.15 },
    duration: { type: Number, default: 1200 },
    delay: { type: Number, default: 0 }
  }

  connect() {
    this.finished = false
    this.started = false
    this.inView = false
    this.elapsed = 0
    this.lastFrame = 0
    this.frameId = null
    this.delayTimer = null
    this.filter = null
    this.defs = null
    if (!PRESETS[this.presetValue]) return
    if (this.element.matches("a, button, input, select, textarea, table") ||
        this.element.querySelector("a, button, input, select, textarea, table, svg text")) return

    this.motion = window.matchMedia("(prefers-reduced-motion: reduce)")
    this.onMotion = () => {
      if (this.motion.matches && this.modeValue === "resolve") this.finish()
    }
    this.motion.addEventListener?.("change", this.onMotion)
    if (this.motion.matches && this.modeValue === "resolve") return

    this.originalFilter = this.element.style.filter
    this.installFilter()
    this.setFidelity(this.modeValue === "hold" && !this.hasFromValue ? 0 : this.fromValue)
    this.element.style.filter = `url(#${this.filter.id})`

    if (this.modeValue === "hold") return
    this.onVisibility = () => this.sync()
    document.addEventListener("visibilitychange", this.onVisibility)
    if (typeof IntersectionObserver === "function") {
      this.observer = new IntersectionObserver(([entry]) => {
        this.inView = entry.isIntersecting
        this.sync()
      }, { threshold: 0.1 })
      this.observer.observe(this.element)
    } else {
      this.inView = true
      this.sync()
    }
  }

  disconnect() {
    this.observer?.disconnect()
    document.removeEventListener("visibilitychange", this.onVisibility)
    this.motion?.removeEventListener?.("change", this.onMotion)
    clearTimeout(this.delayTimer)
    cancelAnimationFrame(this.frameId)
    this.delayTimer = null
    this.frameId = null
    if (this.filter) this.element.style.filter = this.originalFilter
    this.defs?.remove()
  }

  installFilter() {
    const id = `astrolabe-dither-${++nextId}`
    this.defs = document.createElementNS("http://www.w3.org/2000/svg", "svg")
    this.defs.setAttribute("aria-hidden", "true")
    this.defs.style.cssText = "position:absolute;width:0;height:0;overflow:hidden;pointer-events:none"
    this.defs.innerHTML = `<defs><filter id="${id}" x="-12%" y="-12%" width="124%" height="124%" color-interpolation-filters="sRGB">
      <feImage class="tile" x="0" y="0" width="12" height="12" preserveAspectRatio="none" result="tile"/>
      <feTile in="tile" result="screen"/><feColorMatrix in="screen" type="luminanceToAlpha" result="threshold"/>
      <feGaussianBlur class="blur" in="SourceAlpha" stdDeviation="0" result="spread"/>
      <feComponentTransfer in="spread" result="cover"><feFuncA class="gain" type="linear" slope="1" intercept="0"/></feComponentTransfer>
      <feComposite in="cover" in2="threshold" operator="arithmetic" k1="0" k2="1" k3="-1" k4="0" result="diff"/>
      <feComponentTransfer in="diff" result="bits"><feFuncA type="linear" slope="60" intercept="0"/></feComponentTransfer>
      <feComponentTransfer in="SourceGraphic" result="source"><feFuncA class="hard" type="linear" slope="1" intercept="0"/></feComponentTransfer>
      <feComposite in="source" in2="bits" operator="in"/>
    </filter></defs>`
    document.body.append(this.defs)
    this.filter = this.defs.querySelector("filter")
    this.tile = this.filter.querySelector(".tile")
    this.tile.setAttribute("href", ditherTile())
    this.blur = this.filter.querySelector(".blur")
    this.gain = this.filter.querySelector(".gain")
    this.hard = this.filter.querySelector(".hard")
  }

  setFidelity(fidelity) {
    const preset = PRESETS[this.presetValue]
    const amount = Math.max(0, Math.min(1, fidelity))
    const cell = preset.cell[0] + (preset.cell[1] - preset.cell[0]) * Math.pow(amount, 0.85)
    const side = (cell * 4).toFixed(2)
    this.tile.setAttribute("width", side)
    this.tile.setAttribute("height", side)
    this.blur.setAttribute("stdDeviation", (preset.spread * Math.pow(1 - amount, 1.3)).toFixed(3))
    this.gain.setAttribute("slope", preset.hard > 1 ? "1" : (1.35 + 11 * Math.pow(amount, 1.7)).toFixed(2))
    this.hard.setAttribute("slope", String(preset.hard))
  }

  sync() {
    if (this.finished || this.motion.matches) return this.finish()
    if (!this.inView || document.hidden) {
      cancelAnimationFrame(this.frameId)
      this.frameId = null
      this.lastFrame = 0
      return
    }
    if (!this.started && !this.delayTimer) {
      this.delayTimer = setTimeout(() => {
        this.delayTimer = null
        this.started = true
        this.sync()
      }, Math.max(0, this.delayValue))
      return
    }
    if (this.started && !this.frameId) this.frameId = requestAnimationFrame((now) => this.tick(now))
  }

  tick(now) {
    this.frameId = null
    if (this.lastFrame) this.elapsed = (this.elapsed || 0) + now - this.lastFrame
    this.lastFrame = now
    const progress = Math.min((this.elapsed || 0) / Math.max(1, this.durationValue), 1)
    const eased = 1 - Math.pow(1 - progress, 3)
    this.setFidelity(this.fromValue + (1 - this.fromValue) * eased)
    if (progress === 1) this.finish()
    else this.sync()
  }

  finish() {
    if (this.finished) return
    this.finished = true
    cancelAnimationFrame(this.frameId)
    clearTimeout(this.delayTimer)
    if (this.filter) this.element.style.filter = this.originalFilter
    this.observer?.disconnect()
    this.defs?.remove()
  }
}
